import type { IAffiliation, IAuthor, IJournal, IResearchTopic } from "~/types";
import { isoToDateFormatter } from "~/helpers";
import type {
  AffiliationLocation,
  AffiliationLocationAddress,
  Editor,
  EditorAffiliation,
  InteractionStatics,
  JournalSection,
  Publisher
} from "~/types/store";

export const useSchemaService = () => {
  function getAbsoluteUrl(path: string): string {
    if (!path.startsWith("/")) throw Error("The path must start by /");

    return `${baseUrl}${path}`;
  }

  function getFormattedDate(date?: string): string {
    if (!date) return "";
    return isoToDateFormatter(date, "short");
  }

  function publisher(): Publisher {
    return isFrontiers
      ? {
          "@id": getAbsoluteUrl("/#organization"),
          "@type": "NewsMediaOrganization"
        }
      : {
          "@id": tenantPublisherUrl,
          "@type": tenantPublisherType
        };
  }

  function GetJournalSectionSlug(title?: string): string {
    if (!title) return "";

    return getPlainText(title.replace("Frontiers in ", ""))
      .toLocaleLowerCase()
      .trim()
      .replaceAll(" ", "-")
      .replaceAll("/", "");
  }

  function GetOpenJournals(
    journals: IJournal[],
    baseUrl: string
  ): JournalSection[] {
    const journalSectionSchema: JournalSection[] = [];
    if (!journals) return [];
    journals.filter((journal) => {
      if (journal.isOpenForSubmission === true) {
        if (!journal.sections?.length) {
          journalSectionSchema.push({
            "@id": `${baseUrl}/journals/${GetJournalSectionSlug(
              journal.title
            )}`,
            "@type": "PublicationIssue"
          });
        } else {
          journal.sections?.forEach((section) => {
            if (section.isOpenForSubmission) {
              journalSectionSchema.push({
                "@id": `${baseUrl}/journals/${GetJournalSectionSlug(
                  journal.title
                )}/sections/${GetJournalSectionSlug(section.title)}`,
                "@type": "PublicationIssue"
              });
            }
          });
        }
      }
    });
    return journalSectionSchema;
  }

  function GetEditorsSchema(editors: IAuthor[], loopUrl: string): Editor[] {
    const editorsSchema: Editor[] = [];
    editors.forEach((editor) => {
      editorsSchema.push({
        "@id": `${loopUrl}/people/${editor.userId}/overview`,
        "@type": "Person",
        "name": editor.fullName ? editor.fullName : GetFullName(editor),

        "affiliation": GetAffiliationsSchema(editor.affiliations),
        "jobTitle": "Topic Editor"
      });
    });
    return editorsSchema;
  }

  function GetAffiliationsSchema(
    affiliations?: IAffiliation[]
  ): EditorAffiliation[] | undefined {
    if (!affiliations || !(affiliations.length > 0)) return;
    const editorAffiliations: EditorAffiliation[] = [];
    affiliations.forEach((aff) => {
      if (
        IsNullOrWhiteSpace(aff.country) &&
        IsNullOrWhiteSpace(aff.city) &&
        IsNullOrWhiteSpace(aff.organization)
      ) {
        return;
      } else {
        editorAffiliations.push({
          "@type": "Organization",
          "name": IsNullOrWhiteSpace(aff.organization)
            ? undefined
            : aff.organization,
          "location":
            IsNullOrWhiteSpace(aff.city) && IsNullOrWhiteSpace(aff.country)
              ? undefined
              : GetAffiliationLocationSchema(aff)
        });
      }
    });
    return editorAffiliations;
  }

  function GetFullName(editor: IAuthor): string {
    return `${editor.firstName} ${editor.middleName ? editor.middleName : ""} ${
      editor.lastName
    }`;
  }

  function IsNullOrWhiteSpace(value?: string): boolean {
    if (!value) return false;
    return value === null || value.trim().length === 0;
  }

  function GetAffiliationLocationSchema(
    aff: IAffiliation
  ): AffiliationLocation {
    return {
      "@type": "Place",
      "address": GetLocationAddressSchema(aff)
    };
  }

  function GetLocationAddressSchema(
    aff: IAffiliation
  ): AffiliationLocationAddress {
    return {
      "@type": "PostalAddress",
      "addressLocality": aff.city ?? aff.city,
      "addressCountry": aff.country ?? aff.country
    };
  }

  function GetInteractionStatisticsSchema(
    views?: number
  ): InteractionStatics | undefined {
    if (!views || views < 0) return;
    return {
      "@type": "InteractionCounter",
      "interactionType": "http://schema.org/ReadAction",
      "userInteractionCount": views
    };
  }

  const getPlainText = (html?: string): string => {
    html = html || "";
    return html.replace(/(<([^>]+)>)/gi, "").replace(/\n/g, " ");
  };

  const isFrontiers = true; // TODO: we need to get it from another place in the configuration
  const {
    baseUrl,
    loopUrl,
    isImpactMetricsVisible,
    tenantPublisherUrl,
    tenantPublisherType
  } = useRuntimeConfig().public;

  const getResearchTopicSchema = (researchTopic: IResearchTopic): object => {
    const {
      id,
      slug,
      title,
      description,
      onlineSince,
      journals,
      topicEditors,
      impactV5
    } = researchTopic;
    return {
      "@context": "http://schema.org",
      "@type": "Collection",
      "@id": `${baseUrl}/research-topics/${id}/${slug}`,
      "name": title ? getPlainText(useSanitizeHtml(title)) : "",
      "publisher": publisher(),
      "about": description ? getPlainText(useSanitizeHtml(description)) : "",
      "datePublished": getFormattedDate(onlineSince),
      "isPartOf": GetOpenJournals(journals, baseUrl),
      "editor": GetEditorsSchema(topicEditors, loopUrl),
      "interactionStatistic": isImpactMetricsVisible
        ? GetInteractionStatisticsSchema(impactV5?.views)
        : undefined,
      "inLanguage": "en",
      "license": "CC BY 4.0",
      "URL": `${baseUrl}/research-topics/${id}/${slug}`
    };
  };

  return { getResearchTopicSchema };
};
